// NOTE: IMO we really shouldn't be passing JSON response types around, but the reason for making this file is because
// there are at least 5 copies of the `Bonus` interface. Ideally we could be using a shared API hook that returned
// known type data, ideally in the form of an entity struct (i.e. JS class `Bonus`).
import { InfiniteData } from '@tanstack/react-query';

import { BookmarkDTO } from '@bonusly/common-bookmark';
import { Feedback } from '@bonusly/common-feedback';
import type { HttpResponse } from '@bonusly/http-client';
import { ReasonToken, Recipient } from '@bonusly/recipient-entities';

import { User } from './user';

// Enum values
export enum ReactionType {
	Like = 'like',
	Love = 'love',
	Celebrate = 'celebrate',
	Amaze = 'amaze',
	Laugh = 'laugh',
}

export enum BonusPrivacy {
	Private = 'privately_visible',
	Public = 'publicly_visible',
	Team = 'immediate_team',
}

export enum AwardType {
	Award = 'award',
	Birthday = 'birthday',
	Claimable = 'claimable_award',
	Custom = 'custom_yearly_date',
	Welcome = 'welcome',
	Wellness = 'wellness',
	WorkAnniversary = 'work_anniversary',
	NewHire = 'new_hire',
	ExternalRecognition = 'external_recognition',
}

export enum TemplateState {
	Active = 'active',
	Inactive = 'inactive',
}

export enum TemplateType {
	Award = 'award',
	Bot = 'bot',
}

export enum ClaimabilityMode {
	GiveOnly = 'give_only',
}

export enum BudgetPeriod {
	Month = 'month',
	Quarter = 'quarter',
	Week = 'week',
	Year = 'year',
}

export enum BonusAttrSymbol {
	Amount = '+',
	Emoji = ':',
	Hashtag = '#',
	Link = '[',
	Recipient = '@',
	UserTag = '&',
}

// Interfaces

export type BonusVisualState = {
	gift_wrapped: boolean;
	gift_wrap_style?: string;
	gift_wrap_color?: string;
	custom_image_url?: string;
};

export type Bonus = {
	_id?: string;
	id: string;
	add_on_amount_for_feed?: number;
	amount: number;
	amount_with_currency: string;
	award_type: AwardType | null;
	attachments: GenericAttachment[];
	bookmark?: BookmarkDTO;
	child_bonuses: Bonus[];
	created_at: string;
	editable: boolean;
	editable_until: string;
	evaluations?: Feedback[];
	family_amount: number;
	giver: User;
	proxy_giver_id: string | null;
	hide_giver: boolean;
	include_parent_giver: boolean;
	initiator: User | null;
	parent_bonus_id: string | null;
	privacy: BonusPrivacy;
	reactions: Reaction[];
	reason: string;
	reason_html: string;
	reason_tokens?: ReasonToken[];
	receivers: User[];
	tagged_users: User[];
	traits?: FeedbackTrait[];
	recipients?: Recipient[];
	image_url: string | null;
	icon_url: string | null;
	visual_state?: BonusVisualState;
};

export interface SearchBonus {
	amount: number;
	created_at: string;
	giver: User;
	hashtags: string[];
	objectID: string;
	reason: string;
	reason_html: string;
	receivers: User[];
}

export type FeedbackTrait = {
	name: 'feedback_candidate';
	certainty: number;
	data: {
		cta: string;
		give_cta: string;
		prompt: string;
		subjects: string[];
	};
};

export type GenericAttachment = {
	_id?: string;
	id: string;
	category: string;
	name?: string;

	gif: string;
	mp4: string;
	alt_text?: string;
	height?: number;
	width?: number;
	video_id?: string;
	video_thumbnail_url?: string;
	video_poster_url?: string;
	video_transcript_url?: string;
	video_hd_url?: string;
	video_sd_url?: string;
	video_original_url?: string;

	// Employee spotlight & Link preview fields
	url?: string;
	employee_spotlight_id?: string;
	employee_spotlight_section_id?: string;
	image_url?: string;
	title?: string;
	description?: string;
	site_name?: string;

	// Audio fields
	audio_id?: string;
	audio_url?: string;
	audio_transcript_url?: string;
	audio_transcript?: string;
	audio_quote?: string;
	audio_duration?: number;
};

export type Reaction = {
	_id: string;
	emoji: string;
	user_id: string;
	user_name: string;
	// NOTE: the recognition-feed endpoint returns reactions with emoji, user_id, id and user object
	id?: string;
	user?: {
		created_at: string;
		display_first_name: string;
		display_name: string;
		email: string;
		first_name: string;
		full_pic_url: string;
		id: string;
		last_name: string;
		preferred_first_name: string;
		profile_pic_url: string;
		updated_at: string;
		username: string;
	};
};

export type CachedBonus = HttpResponse<{ result: Bonus[] }>;

export type CachedBonuses = InfiniteData<HttpResponse<{ result: Bonus[] }>>;

export interface ApprovalProcess {
	id: string;
}

export interface Award {
	id: string;

	approval_process: ApprovalProcess | null;
	allowance: number;
	budget_period: BudgetPeriod;
	created_at: string;
	deleted_at: string | null;
	giving_balance: number;
	reason: string;
	state: TemplateState;
	updated_at: string;
	hide_giver?: boolean;
	bonus_privacy_changeable: boolean;
	bonus_privacy: BonusPrivacy;
	reason_editable?: boolean;
	amount?: number;
	bonus_amount_changeable?: boolean;
	name: string;
	description?: string;

	giver: AwardGiver;
}

export interface ApprovalRequest {
	id: string;

	bonus_reason: string;
	created_at: string;
	claim_reason: string;
	privacy: BonusPrivacy;
	updated_at: string;

	award?: Award;
	giver?: User;
	initiator?: User;
}

export interface ApprovalRequest {
	id: string;

	bonus_reason: string;
	claim_reason: string;
	privacy: BonusPrivacy;

	award?: Award;
	giver?: User;
	initiator?: User;
}

export interface AwardGiver {
	id: string;

	first_name: string;
	infinite_budget: boolean;
}
